import React, { useState, useEffect } from 'react';
import { Calendar, Mail, Clock, FileText, Settings, Plus, Play, Pause, Trash2, Edit, Eye, Download, Upload, Filter, RefreshCw, CheckCircle, XCircle, AlertCircle, Database, Code, Globe, List, History } from 'lucide-react';
import { apiCall, apiCallJson } from './Api';
import { PageLayout, PageHeader, PageBody } from './PageLayout';

const getApiUrl = () => {
  // Check if runtime config exists (set by env-config.sh)
  if (window._env_ && window._env_.REACT_APP_API_URL) {
    return window._env_.REACT_APP_API_URL;
  }
  // Fallback to build-time environment variable
  return process.env.REACT_APP_API_URL || 'http://localhost:8000';
};

const API_BASE = getApiUrl();

// API Service
export const ApiService = () => {

  return {
    async getScheduledReports(params = {}) {
      const query = new URLSearchParams(params).toString();
      return apiCallJson(`/api/scheduler/scheduled-reports?${query}`);
    },

    async createScheduledReport(data) {
      return apiCallJson('/api/scheduler/scheduled-reports', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async toggleScheduledReport(id) {
      return apiCallJson(`/api/scheduler/scheduled-reports/${id}/toggle`, { method: 'PUT' });
    },

    async deleteScheduledReport(id) {
      return apiCallJson(`/api/scheduler/scheduled-reports/${id}`, { method: 'DELETE' });
    },

    async testScheduledReport(id) {
      return apiCallJson(`/api/scheduler/scheduled-reports/${id}/test`, { method: 'POST' });
    },

    async getEmailTemplates() {
      return apiCallJson('/api/scheduler/email-templates');
    },

    async getDefaultParameters() {
      return apiCallJson('/api/scheduler/default-parameters');
    },

    async previewQueryTemplate(template, parameters) {
      return apiCallJson('/api/scheduler/query-templates/preview', {
        method: 'POST',
        body: JSON.stringify({ template, parameters }),
      });
    },

    async export(exportConfig) {
      return apiCallJson('/api/scheduler/export', {
        method: 'POST',
        body: JSON.stringify(exportConfig),
      });
    },

    async exportSync(exportConfig) {
      return apiCallJson('/api/scheduler/export/sync', {
        method: 'POST',
        body: JSON.stringify(exportConfig),
      });
    },

    async getExportStatus(executionId) {
      return apiCallJson(`/api/scheduler/export/status/${executionId}`);
    },

    async getImmediateExecutions(params = {}) {
      const query = new URLSearchParams(params).toString();
      return apiCallJson(`/api/scheduler/immediate-executions?${query}`);
    },

    async getScheduledExecutions(params = {}) {
      const query = new URLSearchParams(params).toString();
      return apiCallJson(`/api/scheduler/scheduled-executions?${query}`);
    },

    getDownloadUrl(executionId, fileType) {
      return `${API_BASE}/api/scheduler/export/download/${executionId}/${fileType}`;
    },

    async createEmailTemplate(data) {
      return apiCallJson('/api/scheduler/email-templates', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateEmailTemplate(id, data) {
      return apiCallJson(`/api/scheduler/email-templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    async deleteEmailTemplate(id) {
      return apiCallJson(`/api/scheduler/email-templates/${id}`, {
        method: 'DELETE',
      });
    },
  };
};

const ReportScheduler = () => {
  const [activeTab, setActiveTab] = useState('reports');
  const [scheduledReports, setScheduledReports] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [immediateExecutions, setImmediateExecutions] = useState([]);
  const [scheduledExecutions, setScheduledExecutions] = useState([]);
  const [defaultParameters, setDefaultParameters] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showEmailTemplateModal, setShowEmailTemplateModal] = useState(false);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState(null);
  const apiService = ApiService();

  useEffect(() => {
    loadData();
    // Auto-refresh executions every 30 seconds
    const interval = setInterval(() => {
      if (activeTab === 'immediate' || activeTab === 'scheduled') {
        loadExecutions();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [activeTab]);

  const showToast = (message, type = 'info') => {
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 p-4 rounded-lg z-50 ${
      type === 'success' ? 'bg-green-500' : 
      type === 'error' ? 'bg-red-500' : 'bg-blue-500'
    } text-white`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportsData, templatesData, defaultParamsData] = await Promise.all([
        apiService.getScheduledReports(),
        apiService.getEmailTemplates(),
        apiService.getDefaultParameters()
      ]);

      setScheduledReports(reportsData);
      setEmailTemplates(templatesData);
      setDefaultParameters(defaultParamsData);
      
      // Load executions if on those tabs
      if (activeTab === 'immediate' || activeTab === 'scheduled') {
        await loadExecutions();
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadExecutions = async () => {
    try {
      if (activeTab === 'immediate') {
        const data = await apiService.getImmediateExecutions({ limit: 50 });
        setImmediateExecutions(data);
      } else if (activeTab === 'scheduled') {
        const data = await apiService.getScheduledExecutions({ limit: 50 });
        setScheduledExecutions(data);
      }
    } catch (error) {
      console.error('Error loading executions:', error);
    }
  };

  const toggleReportStatus = async (reportId) => {
    try {
      await apiService.toggleScheduledReport(reportId);
      
      setScheduledReports(prev => 
        prev.map(r => r.id === reportId ? { ...r, is_active: !r.is_active } : r)
      );
      
      showToast('Report status updated successfully', 'success');
    } catch (error) {
      console.error('Error toggling report status:', error);
      showToast('Failed to update report status', 'error');
    }
  };

  const testReport = async (reportId) => {
    try {
      const result = await apiService.testScheduledReport(reportId);
      showToast(`Test started successfully. Execution ID: ${result.execution_id}`, 'success');
    } catch (error) {
      console.error('Error testing report:', error);
      showToast('Failed to start test: ' + error.message, 'error');
    }
  };

  const deleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report?')) {
      try {
        await apiService.deleteScheduledReport(reportId);
        setScheduledReports(prev => prev.filter(r => r.id !== reportId));
        showToast('Report deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting report:', error);
        showToast('Failed to delete report', 'error');
      }
    }
  };

  const exportReport = async (exportConfig) => {
    try {
      setLoading(true);
      
      if (exportConfig.sync) {
        const result = await apiService.exportSync(exportConfig);
        
        if (result.download_urls) {
          Object.entries(result.download_urls).forEach(([fileType, url]) => {
            const link = document.createElement('a');
            link.href = url;
            link.download = `export_${Date.now()}.${fileType}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          });
          
          showToast('Export completed and downloaded successfully', 'success');
        }
      } else {
        const result = await apiService.export(exportConfig);
        showToast(`Export started successfully. Execution ID: ${result.execution_id}`, 'success');
        pollExportStatus(result.execution_id);
      }
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
      showToast('Failed to start export: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const pollExportStatus = async (executionId, maxAttempts = 60) => {
    let attempts = 0;
    
    const poll = async () => {
      try {
        const status = await apiService.getExportStatus(executionId);
        
        if (status.status === 'completed') {
          showToast('Export completed successfully!', 'success');
          
          if (status.download_urls) {
            showDownloadOptions(status.download_urls, executionId);
          }
          
          // Refresh executions list
          if (activeTab === 'immediate') {
            loadExecutions();
          }
          return;
        } else if (status.status === 'failed') {
          showToast(`Export failed: ${status.error_message || 'Unknown error'}`, 'error');
          return;
        } else if (attempts >= maxAttempts) {
          showToast('Export timeout - please check status manually', 'error');
          return;
        }
        
        attempts++;
        setTimeout(poll, 2000);
        
      } catch (error) {
        console.error('Error polling export status:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
          attempts++;
        }
      }
    };
    
    poll();
  };

  const showDownloadOptions = (downloadUrls, executionId) => {
    // Built with DOM APIs (no innerHTML): URLs and file types are never parsed as markup.
    const box = document.createElement('div');
    box.style.cssText = 'padding: 15px; background: white; border: 1px solid #ccc; border-radius: 5px; position: fixed; top: 20px; right: 20px; z-index: 1000; box-shadow: 0 2px 10px rgba(0,0,0,0.1);';
    const title = document.createElement('h4');
    title.style.margin = '0 0 10px 0';
    title.textContent = 'Export Ready';
    const msg = document.createElement('p');
    msg.style.margin = '0 0 10px 0';
    msg.textContent = 'Your export is ready for download:';
    box.append(title, msg);
    Object.entries(downloadUrls).forEach(([fileType, url]) => {
      let safeUrl = '#';
      try {
        const u = new URL(String(url), window.location.href);
        if (u.protocol === 'http:' || u.protocol === 'https:') safeUrl = u.href;
      } catch (e) { /* keep '#' */ }
      const link = document.createElement('a');
      link.href = safeUrl;
      link.download = `export_${executionId}.${fileType}`;
      link.style.cssText = 'margin-right: 10px; color: blue;';
      link.textContent = `Download ${String(fileType).toUpperCase()}`;
      box.appendChild(link);
    });
    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = 'margin-left: 10px; background: #dc3545; color: white; border: none; padding: 5px 10px; border-radius: 3px; cursor: pointer;';
    closeBtn.onclick = () => box.remove();
    box.appendChild(closeBtn);
    document.body.appendChild(box);

    setTimeout(() => {
      if (box.parentElement) {
        box.remove();
      }
    }, 30000);
  };

  const formatCron = (cronExpression) => {
    const patterns = {
      '0 8 * * *': 'Daily at 8:00 AM',
      '0 9 * * 1': 'Weekly on Monday at 9:00 AM',
      '0 0 1 * *': 'Monthly on 1st at midnight',
      '0 18 * * 5': 'Weekly on Friday at 6:00 PM'
    };
    return patterns[cronExpression] || cronExpression;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDuration = (durationMs) => {
    if (!durationMs) return 'N/A';
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const createEmailTemplate = async (templateData) => {
    try {
      const createdTemplate = await apiService.createEmailTemplate(templateData);
      setEmailTemplates(prev => [...prev, createdTemplate]);
      setShowEmailTemplateModal(false);
      showToast('Email template created successfully', 'success');
    } catch (error) {
      console.error('Error creating email template:', error);
      showToast('Failed to create email template: ' + error.message, 'error');
    }
  };

  const updateEmailTemplate = async (templateId, templateData) => {
    try {
      const updatedTemplate = await apiService.updateEmailTemplate(templateId, templateData);
      setEmailTemplates(prev => prev.map(t => t.id === templateId ? updatedTemplate : t));
      setShowEmailTemplateModal(false);
      showToast('Email template updated successfully', 'success');
    } catch (error) {
      console.error('Error updating email template:', error);
      showToast('Failed to update email template: ' + error.message, 'error');
    }
  };

  const deleteEmailTemplate = async (templateId) => {
    // Check if template is in use
    const isInUse = scheduledReports.some(r => r.template_id === templateId);
    if (isInUse) {
      showToast('Cannot delete template: It is currently being used by scheduled reports', 'error');
      return;
    }

    if (window.confirm('Are you sure you want to delete this email template?')) {
      try {
        await apiService.deleteEmailTemplate(templateId);
        setEmailTemplates(prev => prev.filter(t => t.id !== templateId));
        showToast('Email template deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting email template:', error);
        showToast('Failed to delete email template: ' + error.message, 'error');
      }
    }
  };

  const EmailTemplateModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      subject_template: '',
      html_header: '',
      html_footer: '',
      header_image_url: '',
      footer_image_url: '',
      is_active: true
    });

    useEffect(() => {
      if (editingEmailTemplate) {
        setFormData({
          name: editingEmailTemplate.name,
          subject_template: editingEmailTemplate.subject_template,
          html_header: editingEmailTemplate.html_header,
          html_footer: editingEmailTemplate.html_footer,
          header_image_url: editingEmailTemplate.header_image_url || '',
          footer_image_url: editingEmailTemplate.footer_image_url || '',
          is_active: editingEmailTemplate.is_active
        });
      } else {
        setFormData({
          name: '',
          subject_template: '',
          html_header: '',
          html_footer: '',
          header_image_url: '',
          footer_image_url: '',
          is_active: true
        });
      }
    }, [editingEmailTemplate]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      const templateData = {
        ...formData,
        created_by: 'current_user'
      };

      if (editingEmailTemplate) {
        await updateEmailTemplate(editingEmailTemplate.id, templateData);
      } else {
        await createEmailTemplate(templateData);
      }
    };

    if (!showEmailTemplateModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {editingEmailTemplate ? 'Edit Email Template' : 'Create Email Template'}
              </h3>
              <button
                onClick={() => setShowEmailTemplateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2">Basic Information</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter template name"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="mr-2"
                    />
                    <label htmlFor="is_active" className="text-sm text-gray-700">
                      Active Template
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject Template *</label>
                  <input
                    type="text"
                    required
                    value={formData.subject_template}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject_template: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="{{ report_name }} - Generated on {{ date }}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available variables: report_name, date, generated_at, page_title
                  </p>
                </div>
              </div>

              {/* Images */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2">Images</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Header Image URL</label>
                    <input
                      type="url"
                      value={formData.header_image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, header_image_url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Footer Image URL</label>
                    <input
                      type="url"
                      value={formData.footer_image_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, footer_image_url: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/footer.png"
                    />
                  </div>
                </div>
              </div>

              {/* HTML Content */}
              <div className="space-y-4">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2">HTML Content</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Header HTML *</label>
                  <textarea
                    required
                    value={formData.html_header}
                    onChange={(e) => setFormData(prev => ({ ...prev, html_header: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    rows="6"
                    placeholder="<div style='text-align: center; padding: 20px;'>
    <h1>{{ report_name }}</h1>
    <p>Healthcare Analytics Report</p>
  </div>"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Footer HTML *</label>
                  <textarea
                    required
                    value={formData.html_footer}
                    onChange={(e) => setFormData(prev => ({ ...prev, html_footer: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    rows="4"
                    placeholder="<div style='text-align: center; padding: 20px; color: #666;'>
    <p>Generated on {{ generated_at }}</p>
    <p>&copy; 2025 Healthcare Analytics</p>
  </div>"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setShowEmailTemplateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  {editingEmailTemplate ? 'Update Template' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };


  const ScheduledReportsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Scheduled Reports</h2>
          <p className="text-gray-600"></p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export Now
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Schedule Report
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipients</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scheduledReports.map((report) => {                
                return (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{report.name}</div>
                        <div className="text-sm text-gray-500">{report.description}</div>
                        {report.page_title && (
                          <div className="text-xs text-blue-600">{report.page_title}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {report.export_type === 'route' ? (
                          <>
                            <Globe className="w-4 h-4 text-blue-500" />
                            <span className="text-sm text-gray-900">Route</span>
                          </>
                        ) : (
                          <>
                            <Database className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-gray-900">Query</span>
                          </>
                        )}
                      </div>
                      {report.export_type === 'route' && report.render_url && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {report.render_url}
                        </div>
                      )}
                      {report.export_type === 'query' && (
                        <div className="text-xs text-gray-500">
                          Custom SQL Query
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatCron(report.cron_expression)}</div>
                      {report.next_run_at && (
                        <div className="text-xs text-gray-500">
                          Next: {new Date(report.next_run_at).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{report.recipients.length} recipient(s)</div>
                      <div className="text-xs text-gray-500">
                        {report.recipients.slice(0, 2).join(', ')}
                        {report.recipients.length > 2 && '...'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        report.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {report.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleReportStatus(report.id)}
                          className={`p-1 rounded ${
                            report.is_active 
                              ? 'text-orange-600 hover:bg-orange-100' 
                              : 'text-green-600 hover:bg-green-100'
                          }`}
                          title={report.is_active ? 'Pause' : 'Resume'}
                        >
                          {report.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => testReport(report.id)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          title="Test Report"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteReport(report.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const ImmediateExecutionsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Immediate Exports</h2>
          <p className="text-gray-600"></p>
        </div>
        <button
          onClick={loadExecutions}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Execution ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {immediateExecutions.map((execution) => (
                <tr key={execution.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-mono text-gray-900">
                      {execution.id.substring(0, 8)}...
                    </div>
                    <div className="text-xs text-gray-500">
                      {execution.page_identifier || execution.dashboard_id || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {execution.export_type === 'route' ? (
                        <>
                          <Globe className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-gray-900">Route</span>
                        </>
                      ) : (
                        <>
                          <Database className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-900">Query</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {execution.output_format?.toUpperCase()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(execution.status)}
                      <span className={`text-sm font-medium ${
                        execution.status === 'completed' ? 'text-green-600' :
                        execution.status === 'failed' ? 'text-red-600' :
                        execution.status === 'running' ? 'text-blue-600' :
                        'text-yellow-600'
                      }`}>
                        {execution.status}
                      </span>
                    </div>
                    {execution.error_message && (
                      <div className="text-xs text-red-500 mt-1 truncate max-w-xs">
                        {execution.error_message}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatDuration(execution.duration_ms)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {formatFileSize(execution.file_size_bytes)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {new Date(execution.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(execution.created_at).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {execution.status === 'completed' && (
                        <>
                          {execution.pdf_file_path && (
                            <a
                              href={apiService.getDownloadUrl(execution.id, 'pdf')}
                              download
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              title="Download PDF"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                          {execution.png_file_path && (
                            <a
                              href={apiService.getDownloadUrl(execution.id, 'png')}
                              download
                              className="p-1 text-green-600 hover:bg-green-100 rounded"
                              title="Download PNG"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </>
                      )}
                      {execution.status === 'running' && (
                        <button
                          onClick={() => pollExportStatus(execution.id)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          title="Check Status"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const ScheduledExecutionsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Scheduled Executions</h2>
          <p className="text-gray-600"></p>
        </div>
        <button
          onClick={loadExecutions}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Execution Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scheduledExecutions.map((execution) => {
                const report = scheduledReports.find(r => r.id === execution.scheduled_report_id);
                
                return (
                  <tr key={execution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {report?.name || 'Unknown Report'}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {execution.scheduled_report_id.substring(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(execution.execution_time).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(execution.execution_time).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(execution.status)}
                        <span className={`text-sm font-medium ${
                          execution.status === 'completed' ? 'text-green-600' :
                          execution.status === 'failed' ? 'text-red-600' :
                          execution.status === 'running' ? 'text-blue-600' :
                          'text-yellow-600'
                       }`}>
                         {execution.status}
                       </span>
                     </div>
                     {execution.error_message && (
                       <div className="text-xs text-red-500 mt-1 truncate max-w-xs">
                         {execution.error_message}
                       </div>
                     )}
                   </td>
                   <td className="px-6 py-4">
                     <div className="text-sm text-gray-900">
                       {formatDuration(execution.duration_ms)}
                     </div>
                   </td>
                   <td className="px-6 py-4">
                     <div className="flex items-center gap-2">
                       {execution.email_sent ? (
                         <>
                           <CheckCircle className="w-4 h-4 text-green-500" />
                           <span className="text-sm text-green-600">Sent</span>
                         </>
                       ) : (
                         <>
                           <XCircle className="w-4 h-4 text-red-500" />
                           <span className="text-sm text-red-600">Failed</span>
                         </>
                       )}
                     </div>
                     <div className="text-xs text-gray-500">
                       {execution.recipients_count} recipient(s)
                     </div>
                     {execution.email_sent_at && (
                       <div className="text-xs text-gray-500">
                         {new Date(execution.email_sent_at).toLocaleTimeString()}
                       </div>
                     )}
                   </td>
                   <td className="px-6 py-4">
                     <div className="flex items-center gap-2">
                       {execution.status === 'completed' && (
                         <>
                           {execution.pdf_file_path && (
                             <a
                               href={apiService.getDownloadUrl(execution.id, 'pdf')}
                               download
                               className="p-1 text-red-600 hover:bg-red-100 rounded"
                               title="Download PDF"
                             >
                               <Download className="w-4 h-4" />
                             </a>
                           )}
                           {execution.png_file_path && (
                             <a
                               href={apiService.getDownloadUrl(execution.id, 'png')}
                               download
                               className="p-1 text-green-600 hover:bg-green-100 rounded"
                               title="Download PNG"
                             >
                               <Download className="w-4 h-4" />
                             </a>
                           )}
                         </>
                       )}
                       <button
                         className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                         title="View Details"
                       >
                         <Eye className="w-4 h-4" />
                       </button>
                     </div>
                   </td>
                 </tr>
               );
             })}
           </tbody>
         </table>
       </div>
     </div>
   </div>
 );

 const ExportModal = () => {
   const [exportConfig, setExportConfig] = useState({
     export_type: 'route',
     page_identifier: '',
     render_url: '',
     render_params: {},
     filters: {},
     query_template: '',
     query_parameters: {},
     visualization_config: '{}',
     output_format: 'pdf',
     pdf_orientation: 'portrait',
     pdf_format: 'A4',
     render_wait_time: 5000,
     filename_prefix: '',
     page_title: '',
     sync: false
   });

   const [queryPreview, setQueryPreview] = useState(null);
   const [isPreviewLoading, setIsPreviewLoading] = useState(false);

   const handleSubmit = (e) => {
     e.preventDefault();
     exportReport(exportConfig);
   };

   const handleParameterChange = (key, value) => {
     setExportConfig(prev => ({
       ...prev,
       query_parameters: {
         ...prev.query_parameters,
         [key]: value
       }
     }));
   };

   const previewQuery = async () => {
     if (!exportConfig.query_template) return;

     setIsPreviewLoading(true);
     try {
       const result = await apiService.previewQueryTemplate(
         exportConfig.query_template,
         exportConfig.query_parameters
       );
       setQueryPreview(result);
     } catch (error) {
       console.error('Error previewing query:', error);
       showToast('Failed to preview query', 'error');
     } finally {
       setIsPreviewLoading(false);
     }
   };

   if (!showExportModal) return null;

   return (
     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
       <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
         <div className="p-6">
           <div className="flex justify-between items-center mb-6">
             <h3 className="text-lg font-medium text-gray-900">Create Export</h3>
             <button
               onClick={() => setShowExportModal(false)}
               className="text-gray-400 hover:text-gray-600"
             >
               ×
             </button>
           </div>

           <form onSubmit={handleSubmit} className="space-y-6">
             {/* Export Type Selection */}
             <div className="space-y-4">
               <h4 className="text-md font-medium text-gray-900">Export Type</h4>
               
               <div className="grid grid-cols-2 gap-4">
                 <div
                   className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                     exportConfig.export_type === 'route'
                       ? 'border-blue-500 bg-blue-50'
                       : 'border-gray-200 hover:border-gray-300'
                   }`}
                   onClick={() => setExportConfig(prev => ({ ...prev, export_type: 'route' }))}
                 >
                   <div className="flex items-center gap-3">
                     <Globe className="w-6 h-6 text-blue-500" />
                     <div>
                       <h5 className="font-medium text-gray-900">Route-based</h5>
                       <p className="text-sm text-gray-500">Export from a URL/page</p>
                     </div>
                   </div>
                 </div>

                 <div
                   className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                     exportConfig.export_type === 'query'
                       ? 'border-blue-500 bg-blue-50'
                       : 'border-gray-200 hover:border-gray-300'
                   }`}
                   onClick={() => setExportConfig(prev => ({ ...prev, export_type: 'query' }))}
                 >
                   <div className="flex items-center gap-3">
                     <Database className="w-6 h-6 text-green-500" />
                     <div>
                       <h5 className="font-medium text-gray-900">Query-based</h5>
                       <p className="text-sm text-gray-500">Export from SQL query</p>
                     </div>
                   </div>
                 </div>
               </div>
             </div>

             {/* Route-based Configuration */}
             {exportConfig.export_type === 'route' && (
               <div className="space-y-4">
                 <h4 className="text-md font-medium text-gray-900">Route Configuration</h4>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Page URL *</label>
                   <input
                     type="url"
                     required
                     value={exportConfig.render_url}
                     onChange={(e) => setExportConfig(prev => ({ ...prev, render_url: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     placeholder="https://example.com/dashboard"
                   />
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Page Identifier</label>
                     <input
                       type="text"
                       value={exportConfig.page_identifier}
                       onChange={(e) => setExportConfig(prev => ({ ...prev, page_identifier: e.target.value }))}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       placeholder="dashboard-id"
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Wait Time (ms)</label>
                     <input
                       type="number"
                       value={exportConfig.render_wait_time}
                       onChange={(e) => setExportConfig(prev => ({ ...prev, render_wait_time: parseInt(e.target.value) }))}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                       min="1000"
                       max="30000"
                     />
                   </div>
                 </div>
               </div>
             )}

             {/* Query-based Configuration */}
             {exportConfig.export_type === 'query' && (
               <div className="space-y-4">
                 <h4 className="text-md font-medium text-gray-900">Query Configuration</h4>
                 
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">SQL Query Template *</label>
                   <textarea
                     required
                     value={exportConfig.query_template}
                     onChange={(e) => setExportConfig(prev => ({ ...prev, query_template: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                     rows="6"
                     placeholder="SELECT * FROM table WHERE date = '{{ current_date }}' AND tenant_id = '{{ tenant_id }}'"
                   />
                 </div>

                 <div className="bg-gray-50 p-3 rounded-lg">
                   <div className="flex items-center justify-between mb-2">
                     <h5 className="text-sm font-medium text-gray-700">Available Default Parameters</h5>
                     <button
                       type="button"
                       onClick={previewQuery}
                       disabled={isPreviewLoading}
                       className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                     >
                       {isPreviewLoading ? 'Loading...' : 'Preview Query'}
                     </button>
                   </div>
                   <div className="grid grid-cols-3 gap-2 text-xs">
                     {Object.entries(defaultParameters).map(([key, value]) => (
                       <div key={key} className="bg-white p-2 rounded">
                         <div className="font-mono text-blue-600">{ key }</div>
                         <div className="text-gray-500 truncate">{String(value)}</div>
                       </div>
                     ))}
                   </div>
                 </div>

                 {queryPreview && (
                   <div className="bg-gray-50 p-3 rounded-lg">
                     <h5 className="text-sm font-medium text-gray-700 mb-2">Query Preview</h5>
                     {queryPreview.success ? (
                       <div>
                         <div className="text-sm text-green-600 mb-2">
                           ✓ Query executed successfully. Found {queryPreview.total_rows} rows.
                         </div>
                         {queryPreview.data.length > 0 && (
                           <div className="overflow-x-auto">
                             <table className="min-w-full text-xs">
                               <thead>
                                 <tr className="bg-gray-100">
                                   {queryPreview.columns.map(col => (
                                     <th key={col} className="px-2 py-1 text-left">{col}</th>
                                   ))}
                                 </tr>
                               </thead>
                               <tbody>
                                 {queryPreview.data.slice(0, 3).map((row, idx) => (
                                   <tr key={idx} className="border-t">
                                     {queryPreview.columns.map(col => (
                                       <td key={col} className="px-2 py-1">{String(row[col] || '')}</td>
                                     ))}
                                   </tr>
                                 ))}
                               </tbody>
                             </table>
                           </div>
                         )}
                       </div>
                     ) : (
                       <div className="text-sm text-red-600">
                         ✗ Query failed: {queryPreview.error}
                       </div>
                     )}
                   </div>
                 )}
               </div>
             )}

             {/* Common Configuration */}
             <div className="space-y-4">
               <h4 className="text-md font-medium text-gray-900">Output Configuration</h4>
               
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                   <select
                     value={exportConfig.output_format}
                     onChange={(e) => setExportConfig(prev => ({ ...prev, output_format: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="pdf">PDF</option>
                     <option value="png">PNG</option>
                     <option value="both">Both PDF & PNG</option>
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                   <input
                     type="text"
                     value={exportConfig.page_title}
                     onChange={(e) => setExportConfig(prev => ({ ...prev, page_title: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     placeholder="Report Title"
                   />
                 </div>
               </div>

               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">PDF Orientation</label>
                   <select
                     value={exportConfig.pdf_orientation}
                     onChange={(e) => setExportConfig(prev => ({ ...prev, pdf_orientation: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="portrait">Portrait</option>
                     <option value="landscape">Landscape</option>
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">PDF Format</label>
                   <select
                     value={exportConfig.pdf_format}
                     onChange={(e) => setExportConfig(prev => ({ ...prev, pdf_format: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="A4">A4</option>
                     <option value="A3">A3</option>
                     <option value="letter">Letter</option>
                   </select>
                 </div>
               </div>

               <div className="flex items-center">
                 <input
                   type="checkbox"
                   id="sync"
                   checked={exportConfig.sync}
                   onChange={(e) => setExportConfig(prev => ({ ...prev, sync: e.target.checked }))}
                   className="mr-2"
                 />
                 <label htmlFor="sync" className="text-sm text-gray-700">
                   Download immediately (synchronous export)
                 </label>
               </div>
             </div>

             <div className="flex gap-3 pt-6">
               <button
                 type="button"
                 onClick={() => setShowExportModal(false)}
                 className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
               >
                 Cancel
               </button>
               <button
                 type="submit"
                 disabled={loading}
                 className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
               >
                 {loading ? (
                   <>
                     <RefreshCw className="w-4 h-4 animate-spin" />
                     Exporting...
                   </>
                 ) : (
                   <>
                     <Download className="w-4 h-4" />
                     Start Export
                   </>
                 )}
               </button>
             </div>
           </form>
         </div>
       </div>
     </div>
   );
 };

 const CreateReportModal = () => {
   const [formData, setFormData] = useState({
     name: '',
     description: '',
     export_type: 'route',
     template_id: '',
     recipients: '',
     cron_expression: '0 8 * * *',
     timezone: 'UTC',
     render_url: '',
     render_wait_time: 5000,
     query_template: '',
     query_parameters: {},
     page_title: '',
     output_format: 'pdf',
     pdf_orientation: 'portrait',
     pdf_format: 'A4'
   });

   const handleSubmit = async (e) => {
     e.preventDefault();
     
     try {
       const reportData = {
         ...formData,
         recipients: formData.recipients.split(',').map(email => email.trim()).filter(Boolean),
         cc_recipients: [],
         render_params: {},
         filters: {},
         visualization_config: '{}',
         page_identifier: '',
         date_range_type: 'relative',
         relative_days: 1,
         is_active: true,
         created_by: 'current_user'
       };
       
       const createdReport = await apiService.createScheduledReport(reportData);
       setScheduledReports(prev => [...prev, createdReport]);
       setShowCreateModal(false);
       showToast('Scheduled report created successfully', 'success');
       
       // Reset form
       setFormData({
         name: '',
         description: '',
         export_type: 'route',
         template_id: '',
         recipients: '',
         cron_expression: '0 8 * * *',
         timezone: 'UTC',
         render_url: '',
         render_wait_time: 5000,
         query_template: '',
         query_parameters: {},
         page_title: '',
         output_format: 'pdf',
         pdf_orientation: 'portrait',
         pdf_format: 'A4'
       });
       
     } catch (error) {
       console.error('Error creating report:', error);
       showToast('Failed to create report: ' + error.message, 'error');
     }
   };

   if (!showCreateModal) return null;

   return (
     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
       <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
         <div className="p-6">
           <div className="flex justify-between items-center mb-6">
             <h3 className="text-lg font-medium text-gray-900">Create Scheduled Report</h3>
             <button
               onClick={() => setShowCreateModal(false)}
               className="text-gray-400 hover:text-gray-600"
             >
               ×
             </button>
           </div>

           <form onSubmit={handleSubmit} className="space-y-6">
             {/* Basic Information */}
             <div className="space-y-4">
               <h4 className="text-md font-medium text-gray-900 border-b pb-2">Basic Information</h4>
               
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
                 <input
                   type="text"
                   required
                   value={formData.name}
                   onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   placeholder="Enter report name"
                 />
               </div>

               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                 <textarea
                   value={formData.description}
                   onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   rows="2"
                   placeholder="Enter report description"
                 />
               </div>

               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Export Type *</label>
                   <select
                     required
                     value={formData.export_type}
                     onChange={(e) => setFormData(prev => ({ ...prev, export_type: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="route">Route-based</option>
                     <option value="query">Query-based</option>
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Email Template *</label>
                   <select
                     required
                     value={formData.template_id}
                     onChange={(e) => setFormData(prev => ({ ...prev, template_id: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="">Select template</option>
                     {emailTemplates.map(template => (
                       <option key={template.id} value={template.id}>
                         {template.name}
                       </option>
                     ))}
                   </select>
                 </div>
               </div>
             </div>

             {/* Export Configuration */}
             <div className="space-y-4">
               <h4 className="text-md font-medium text-gray-900 border-b pb-2">Export Configuration</h4>
               
               {formData.export_type === 'route' ? (
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Page URL *</label>
                   <input
                     type="url"
                     required
                     value={formData.render_url}
                     onChange={(e) => setFormData(prev => ({ ...prev, render_url: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     placeholder="https://example.com/dashboard"
                   />
                 </div>
               ) : (
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">SQL Query Template *</label>
                   <textarea
                     required
                     value={formData.query_template}
                     onChange={(e) => setFormData(prev => ({ ...prev, query_template: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                     rows="4"
                     placeholder="SELECT * FROM table WHERE date = '{{ current_date }}' AND tenant_id = '{{ tenant_id }}'"
                   />
                 </div>
               )}

               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                 <input
                   type="text"
                   value={formData.page_title}
                   onChange={(e) => setFormData(prev => ({ ...prev, page_title: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   placeholder="Report title for email and file"
                 />
               </div>
             </div>

             {/* Email Configuration */}
             <div className="space-y-4">
               <h4 className="text-md font-medium text-gray-900 border-b pb-2">Email Configuration</h4>
               
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Recipients * (comma-separated emails)</label>
                 <input
                   type="text"
                   required
                   value={formData.recipients}
                   onChange={(e) => setFormData(prev => ({ ...prev, recipients: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   placeholder="email1@example.com, email2@example.com"
                 />
               </div>
             </div>

             {/* Schedule Configuration */}
             <div className="space-y-4">
               <h4 className="text-md font-medium text-gray-900 border-b pb-2">Schedule Configuration</h4>
               
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Schedule *</label>
                   <select
                     value={formData.cron_expression}
                     onChange={(e) => setFormData(prev => ({ ...prev, cron_expression: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="0 8 * * *">Daily at 8:00 AM</option>
                     <option value="0 9 * * 1">Weekly on Monday at 9:00 AM</option>
                     <option value="0 0 1 * *">Monthly on 1st at midnight</option>
                     <option value="0 18 * * 5">Weekly on Friday at 6:00 PM</option>
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                   <select
                     value={formData.timezone}
                     onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="UTC">UTC</option>
                     <option value="America/New_York">Eastern Time</option>
                     <option value="America/Chicago">Central Time</option>
                     <option value="America/Denver">Mountain Time</option>
                     <option value="America/Los_Angeles">Pacific Time</option>
                   </select>
                 </div>
               </div>
             </div>

             {/* Output Configuration */}
             <div className="space-y-4">
               <h4 className="text-md font-medium text-gray-900 border-b pb-2">Output Configuration</h4>
               
               <div className="grid grid-cols-3 gap-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                   <select
                     value={formData.output_format}
                     onChange={(e) => setFormData(prev => ({ ...prev, output_format: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="pdf">PDF</option>
                     <option value="png">PNG</option>
                     <option value="both">Both</option>
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">PDF Orientation</label>
                   <select
                     value={formData.pdf_orientation}
                     onChange={(e) => setFormData(prev => ({ ...prev, pdf_orientation: e.target.value }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   >
                     <option value="portrait">Portrait</option>
                     <option value="landscape">Landscape</option>
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Wait Time (ms)</label>
                   <input
                     type="number"
                     value={formData.render_wait_time}
                     onChange={(e) => setFormData(prev => ({ ...prev, render_wait_time: parseInt(e.target.value) }))}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     min="1000"
                     max="30000"
                   />
                 </div>
               </div>
             </div>

             <div className="flex gap-3 pt-6 border-t">
               <button
                 type="button"
                 onClick={() => setShowCreateModal(false)}
                 className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
               >
                 Cancel
               </button>
               <button
                 type="submit"
                 className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
               >
                 Create Report
               </button>
             </div>
           </form>
         </div>
       </div>
     </div>
   );
 };

 return (
    <PageLayout>
      <PageHeader title="Report Scheduler" icon={Calendar} />
      <PageBody padded={false} className="p-6">

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow mb-6 p-2 mt-8">
       <div className="">
         <nav className="flex gap-8">
           {[
             { id: 'reports', label: 'Scheduled Reports', icon: FileText },
             { id: 'immediate', label: 'Immediate Exports', icon: List },
             { id: 'scheduled', label: 'Scheduled Executions', icon: History },
             { id: 'templates', label: 'Email Templates', icon: Mail }
           ].map(({ id, label, icon: Icon }) => (
             <button
               key={id}
               onClick={() => setActiveTab(id)}
               className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                 activeTab === id
                   ? 'border-blue-500 text-blue-600'
                   : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
               }`}
             >
               <Icon className="w-4 h-4" />
               {label}
             </button>
           ))}
         </nav>
       </div>
     </div>

     {/* Main Content */}
     <div className="bg-white rounded-lg shadow mb-6 p-8 mt-8">
       {loading ? (
         <div className="flex justify-center items-center py-12">
           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
         </div>
       ) : (
         <>
           {activeTab === 'reports' && <ScheduledReportsTab />}
           {activeTab === 'immediate' && <ImmediateExecutionsTab />}
           {activeTab === 'scheduled' && <ScheduledExecutionsTab />}
           {activeTab === 'templates' && (
             <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Email Templates</h2>
                  <p className="text-gray-600"></p>
                </div>
                <button 
                  onClick={() => {
                    setEditingEmailTemplate(null);
                    setShowEmailTemplateModal(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Template
                </button>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {emailTemplates.map((template) => (
                  <div key={template.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                        <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                          {template.subject_template}
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-500">
                        Used in {scheduledReports.filter(r => r.template_id === template.id).length} scheduled report(s)
                      </div>
                      
                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            setEditingEmailTemplate(template);
                            setShowEmailTemplateModal(true);
                          }}
                          className="flex-1 bg-blue-50 text-blue-700 px-3 py-2 rounded text-sm hover:bg-blue-100 transition-colors"
                        >
                          <Edit className="w-4 h-4 inline mr-1" />
                          Edit
                        </button>
                        <button 
                          onClick={() => deleteEmailTemplate(template.id)}
                          className="flex-1 bg-red-50 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-100 transition-colors"
                          disabled={scheduledReports.some(r => r.template_id === template.id)}
                          title={scheduledReports.some(r => r.template_id === template.id) ? "Cannot delete: Template is in use" : "Delete template"}
                        >
                          <Trash2 className="w-4 h-4 inline mr-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
           )}
         </>
       )}
     </div>

     {/* Modals */}
     <CreateReportModal />
     <ExportModal />
     <EmailTemplateModal />
   </PageBody>
    </PageLayout>
 );
};

export default ReportScheduler;